import { type CertificateProgram } from '@mc/api'
import { type Mccerts2664Data } from 'pages/certificates/utils/getMccerts2664Variables'

/**
 * mccerts-2664_v1 — Certificate PDP copy test (variant data transform).
 *
 * Copy + hero imagery on this page are API-sourced on the `program`
 * (CertificateProgram), not hardcoded. This pure helper returns a NEW program
 * with the conversion-relevant fields overridden from the GrowthBook experiment
 * variables (`Mccerts2664Data`). The original `program` is left untouched (used
 * by <Meta>, <Head><title>, tracking, and the nav). It is only applied when the
 * server resolved variant variables for this cert, so control renders
 * identically to today.
 */
export const applyMccerts2664Overrides = (
  program: CertificateProgram,
  data: Mccerts2664Data,
): CertificateProgram => {
  // Short-circuit when there's nothing to override (e.g. the configured control
  // arm resolves an empty `{}`): return the original `program` unchanged so we
  // preserve referential identity and skip allocating a new object on this
  // high-traffic SSR page.
  const hasOverrides =
    !!data.eyebrow ||
    !!data.headline ||
    !!data.subheadline ||
    !!data.images?.hero1x1 ||
    !!data.outcomes?.length
  if (!hasOverrides) {
    return program
  }

  // Build a fresh heroSection so forced, non-empty copy wins over the
  // client-side `heroSection?.headline || programTitle` / `?? programSubtitle`
  // fallbacks. Empty/absent values fall back to the existing copy.
  const heroSection = {
    ...program.heroSection,
    eyebrow: data.eyebrow || program.heroSection?.eyebrow,
    headline: data.headline || program.heroSection?.headline,
    subheadline: data.subheadline || program.heroSection?.subheadline,
  }

  // Hero visual. HeroSection renders program.images.hero1x1 (1:1, square) as the
  // visible hero — that is the field we override, only when the variant supplies
  // an image URL.
  const images = data.images?.hero1x1
    ? {
        ...program.images,
        hero1x1: {
          ...program.images?.hero1x1,
          url: data.images.hero1x1.url,
          altText:
            data.images.hero1x1.altText ?? program.images?.hero1x1?.altText,
        },
      }
    : program.images

  // Map over the EXISTING outcomeStatements so the variant list length always
  // matches control. Each slot falls back to the ORIGINAL control text when the
  // variant has no override at that index, so nothing leaks if control has more
  // items than the variant provides.
  const outcomes = program.outcomes
    ? {
        ...program.outcomes,
        outcomeStatements: program.outcomes.outcomeStatements?.map(
          (statement, index) => ({
            ...statement,
            text: data.outcomes?.[index] ?? statement.text,
          }),
        ),
      }
    : program.outcomes

  return { ...program, heroSection, images, outcomes }
}
